import type { EnrichedError } from "@web-monorepo/telemetry";
import { addBreadcrumb } from "@web-monorepo/telemetry";
import { sanitizeIds } from "./sanitizeIds";

const DOJO_FETCH_MARKER = Symbol.for("dojo.fetch.handled");

export { sanitizeIds } from "./sanitizeIds";
export { fetchResource } from "./fetchResource";
export { fetchText } from "./fetchText";
export { fetchBlobUrl } from "./fetchBlobUrl";
export { downloadFile } from "./downloadFile";
export { uploadToUrl } from "./uploadToUrl";
export { ResourceFetchError, type FetchResult } from "./resourceTypes";

export interface DojoFetchOptions extends RequestInit {
  path?: string;
  route?: string;
}

const ALLOWED_HEADER_KEYS = ["content-type", "x-client-identifier", "x-client-version", "x-sign-attachment-urls"];

function buildSafeHeaders(headers: HeadersInit | undefined): Record<string, string> {
  const safe: Record<string, string> = {};
  if (headers) {
    const h = new Headers(headers);
    for (const key of ALLOWED_HEADER_KEYS) {
      if (h.has(key)) {
        safe[key] = h.get(key)!;
      }
    }
  }
  return safe;
}

export class NoNetworkError extends Error implements EnrichedError {
  cause: Error;
  contexts?: Record<string, Record<string, unknown>>;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  fingerprint?: string[];

  constructor(
    error: Error,
    urlString: string,
    fetchOptions: RequestInit,
    method: string,
    path: string,
    route?: string,
  ) {
    super(`Network error when fetching ${sanitizeIds(urlString)}`);
    this.name = "NoNetworkError";
    this.cause = error;

    // Exclude body and headers to prevent logging sensitive data (passwords, tokens, PII)
    const { headers, body: _body, ...safeOptions } = fetchOptions;
    const safeHeaders = buildSafeHeaders(headers);

    const sanitizedPath = sanitizeIds(path);
    const sanitizedRoute = route ? sanitizeIds(route) : sanitizedPath;
    const isSafariLoadFailed = error instanceof TypeError && error.message.includes("Load failed");

    this.tags = {
      errorType: isSafariLoadFailed ? "safari_load_failed" : "network_error",
      method,
      route: sanitizedRoute,
    };

    this.extra = {
      path: sanitizedPath,
      originalErrorName: error.name,
      originalErrorMessage: error.message,
    };

    this.fingerprint = isSafariLoadFailed ? ["safari-load-failed"] : ["network-error"];
    this.contexts = {
      request: {
        url: sanitizeIds(urlString),
        ...safeOptions,
        headers: safeHeaders,
      },
    };
  }
}

const WRITE_METHODS = new Set(["POST", "PUT", "PATCH"]);
// 301/302: Browser normally changes POST→GET (the bug we're fixing). We preserve the method.
// 307/308: Browser normally preserves the method. We must handle them here since redirect:"manual"
//          prevents the browser from following them automatically.
// 303: Browser normally follows as GET (dropping body). We handle that below in the loop.
const REDIRECT_STATUSES = new Set([301, 302, 303, 307, 308]);
const MAX_REDIRECTS = 5;

export type RedirectHop = {
  from: string;
  to: string;
  statusCode: number;
  hop: number;
};

export class RedirectLimitError extends Error implements EnrichedError {
  cause: Error;
  contexts?: Record<string, Record<string, unknown>>;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  fingerprint?: string[];
  redirectChain: RedirectHop[];

  constructor(
    redirectChain: RedirectHop[],
    urlString: string,
    fetchOptions: RequestInit,
    method: string,
    path: string,
    route?: string,
  ) {
    super(`Too many redirects (>${MAX_REDIRECTS}) when fetching ${sanitizeIds(urlString)}`);
    this.name = "RedirectLimitError";
    this.cause = new Error(`Too many redirects (>${MAX_REDIRECTS})`);
    this.redirectChain = redirectChain;

    // Exclude body and headers to prevent logging sensitive data (passwords, tokens, PII)
    const { headers, body: _body, ...safeOptions } = fetchOptions;
    const safeHeaders = buildSafeHeaders(headers);

    const sanitizedPath = sanitizeIds(path);
    const sanitizedRoute = route ? sanitizeIds(route) : sanitizedPath;

    this.tags = {
      errorType: "redirect_limit_exceeded",
      method,
      route: sanitizedRoute,
    };

    this.extra = {
      path: sanitizedPath,
      maxRedirects: MAX_REDIRECTS,
      redirectCount: redirectChain.length,
    };

    this.fingerprint = ["redirect-limit-exceeded"];
    this.contexts = {
      request: {
        url: sanitizeIds(urlString),
        ...safeOptions,
        headers: safeHeaders,
      },
      redirectChain: Object.fromEntries(
        redirectChain.map((hop, i) => [`hop_${i + 1}`, `${hop.statusCode} ${hop.from} -> ${hop.to}`]),
      ),
    };
  }
}

function resolveUrl(locationHeader: string, baseUrl: string): string {
  try {
    return new URL(locationHeader, baseUrl).toString();
    // eslint-disable-next-line no-catch-all/no-catch-all
  } catch {
    return locationHeader;
  }
}

function isSameOrigin(url1: string, url2: string): boolean {
  try {
    const a = new URL(url1);
    const b = new URL(url2);
    return a.origin === b.origin;
    // eslint-disable-next-line no-catch-all/no-catch-all
  } catch {
    return false;
  }
}

function inputToString(input: RequestInfo | URL): string {
  return typeof input === "string"
    ? input
    : input instanceof URL
      ? input.toString()
      : "url" in input
        ? input.url
        : String(input);
}

// eslint-disable-next-line complexity
export async function dojoFetch(input: RequestInfo | URL, init?: DojoFetchOptions): Promise<Response> {
  const { path: _path, route: _route, ...requestInit } = init ?? {};
  const method = (init?.method || "GET").toUpperCase();
  const isWriteMethod = WRITE_METHODS.has(method);

  try {
    let currentUrl = inputToString(input);
    const markedInit = { ...requestInit, [DOJO_FETCH_MARKER]: true };
    // NOTE: fetchInit.body is reused for every redirect hop.
    // This works correctly for string/ArrayBuffer bodies (the common case in this codebase).
    // If body is a ReadableStream it would be consumed on the first hop; avoid streaming bodies
    // with endpoints that may redirect.
    let fetchInit: RequestInit = isWriteMethod ? { ...markedInit, redirect: "manual" } : markedInit;
    let response: Response;

    if (isWriteMethod) {
      const redirectChain: RedirectHop[] = [];

      for (let hop = 0; hop <= MAX_REDIRECTS; hop++) {
        // eslint-disable-next-line @web-monorepo/no-bare-fetch -- dojoFetch is the fetch wrapper itself; it must call native fetch
        response = await fetch(currentUrl, fetchInit);

        if (!REDIRECT_STATUSES.has(response.status)) {
          return response;
        }

        const location = response.headers.get("location");
        if (!location) {
          return response;
        }

        const nextUrl = resolveUrl(location, currentUrl);

        // Only follow same-origin redirects to prevent leaking request body to external domains
        if (!isSameOrigin(currentUrl, nextUrl)) {
          addBreadcrumb({
            category: "fetch.redirect",
            message: `${method} redirect (${response.status}) blocked — cross-origin`,
            data: { from: sanitizeIds(currentUrl), to: sanitizeIds(nextUrl), statusCode: response.status, method },
            level: "warning",
          });
          return response;
        }

        const hopInfo: RedirectHop = {
          from: sanitizeIds(currentUrl),
          to: sanitizeIds(nextUrl),
          statusCode: response.status,
          hop: hop + 1,
        };
        redirectChain.push(hopInfo);

        addBreadcrumb({
          category: "fetch.redirect",
          message: `${method} redirect (${response.status})`,
          data: {
            ...hopInfo,
            method,
          },
          level: "info",
        });

        currentUrl = nextUrl;

        // 303 See Other: follow as GET without body (per RFC 7231 §6.4.4)
        if (response.status === 303) {
          fetchInit = { ...fetchInit, method: "GET", body: undefined };
        }
      }

      // Exceeded max redirects
      throw new RedirectLimitError(
        redirectChain,
        currentUrl,
        requestInit,
        method,
        init?.path || inputToString(input),
        init?.route,
      );
    }

    // eslint-disable-next-line @web-monorepo/no-bare-fetch -- dojoFetch is the fetch wrapper itself; it must call native fetch
    return await fetch(input, markedInit);
  } catch (error: unknown) {
    if (error instanceof NoNetworkError || error instanceof RedirectLimitError) {
      throw error;
    }
    if (error && typeof error === "object" && "name" in error && error.name === "AbortError") {
      throw error;
    }

    const urlString = inputToString(input);
    const path = init?.path || urlString;
    const wrappedError = error instanceof Error ? error : new Error(String(error));
    throw new NoNetworkError(wrappedError, urlString, requestInit, method, path, init?.route);
  }
}
